import Style from './GeneralFormFillingModal.module.scss';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { ScoreContextProvider } from '../context/score.provider';
import { Editor, Frame } from '@craftjs/core';
import { AttachmentsContextProvider } from '../context/attachments.provider';
import { FormProvider, useForm } from 'react-hook-form';
import {
  GeneralFormFillerProps,
  ProcessAnswerFuncType,
  UploadFileFuncType,
  UploadFilesResolveType,
} from './GeneralFormFillingModal.t';
import { studyNoteFillControlsResolver } from '../studyNoteForms/fillingResolver';
import { calculateSimpleExpressions } from '../monaco/executor';
import { FileParameter, IScoreResult } from 'src/services/api/api-client';
import { FormFillingInitialData } from './useGeneralFormFillingModal';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { isNil } from 'lodash';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';
import { findFilesWithPaths, getFileIdsToCommit, injectFileIdToAnswer, normalizeFileValues } from './helpers';
import { logger } from 'src/application/logging/logging';

export const GeneralFormFillingModal: FC<{
  onClose: () => void;
  initialData: FormFillingInitialData | undefined;
  processAnswerFunc: ProcessAnswerFuncType | undefined;
  uploadFileFunc: UploadFileFuncType | undefined;
}> = ({ onClose, processAnswerFunc, uploadFileFunc, initialData }) => {
  const { t: tCommon } = useTranslation();
  const { t } = useScopedTranslation('GeneralFormBuilder.Errors');
  const isPreviewMode = !processAnswerFunc || !uploadFileFunc;

  // NOTE: temp solution
  const modalSubtitle = isNil(initialData?.resultId) ? tCommon('Common_Create') : tCommon('Common_Edit');

  const [isFinished, setIsFinished] = useState(false);

  const form = useForm<DataType>({
    defaultValues: initialData?.initialValue ?? {},
  });

  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
  } = form;

  const uploadFiles = useCallback(
    async (data: DataType) => {
      const filesToUpload = findFilesWithPaths(data);

      const uploadFilesPromises = filesToUpload.map(
        ({ path, file }) =>
          new Promise<UploadFilesResolveType>(async (resolve, reject) => {
            if (!uploadFileFunc || !processAnswerFunc)
              throw new Error("'uploadFileFunc' or 'processAnswerFunc' are not provided");

            try {
              const fileId = await uploadFileFunc({
                data: file,
                fileName: file.name,
              } satisfies FileParameter);

              const fileFromForm: AppAttachmentValue = form.getValues(path);
              fileFromForm.id = fileId;
              form.setValue(path, fileFromForm);

              resolve({ path: path, id: fileId });
            } catch (error: any) {
              form.setError(path, { message: `${file.name}: ${error.detail ?? t('FailedLoadFile')}` });
              reject(error);
            }
          }),
      );

      return Promise.all(uploadFilesPromises);
    },
    [form, processAnswerFunc, t, uploadFileFunc],
  );

  const triggerSubmit = handleSubmit(async (data: DataType) => {
    if (!uploadFileFunc || !processAnswerFunc)
      throw new Error("'uploadFileFunc' or 'processAnswerFunc' are not provided");

    const scoreResults: IScoreResult[] =
      initialData?.scoreSections?.map(
        (section) =>
          ({
            value: calculateSimpleExpressions(section, data, initialData?.layout, initialData.variables),
            name: section.name,
          } satisfies IScoreResult),
      ) ?? [];

    const loadedFileIds = await uploadFiles(data);
    const dataWithInjectedFileIds = injectFileIdToAnswer(data, loadedFileIds);
    const fileIdsToCommit = getFileIdsToCommit(data);
    const normalizedData = normalizeFileValues(dataWithInjectedFileIds);

    try {
      await processAnswerFunc(normalizedData, scoreResults, fileIdsToCommit);
      setIsFinished(true);
      await new Promise((resolve) => setTimeout(resolve, 1400));
      onClose();
    } catch (error: any) {
      logger().error(error.message, error);
      form.setError('root', { message: error.detail ?? t('CommonError') });
    }
  });

  const confirmationModal = useConfirmationModal({ containerClassName: Style.confirmationModal });
  const onCloseHandler = useCallback(async () => {
    if (!isDirty || isPreviewMode) {
      onClose();
      return;
    }

    const result = await confirmationModal.open({
      title: tCommon('Forms.ConfirmationModal.Header'),
      text: tCommon('Forms.ConfirmationModal.MainText'),
      cancelButtonColorSchema: 'destroy',
      cancelButtonText: tCommon('Forms.ConfirmationModal.DiscardBtn'),
      okButtonText: tCommon('Common_Save'),
    });
    if (result === ConfirmResult.Confirm) triggerSubmit();
    if (result === ConfirmResult.Cancel) onClose();
  }, [isDirty, isPreviewMode, confirmationModal, tCommon, triggerSubmit, onClose]);

  return (
    <>
      <DialogModal
        bodyClassName={Style.modalBody}
        visible={true}
        onHide={onCloseHandler}
        title={initialData?.modalTitle}
        subTitle={modalSubtitle}
        isDisabled={isSubmitting}
        footer={
          isPreviewMode
            ? undefined
            : {
                errors: errors.root?.message,
                leftButton: {
                  text: tCommon('Common_Cancel'),
                  onClick: onCloseHandler,
                  disabled: isSubmitting,
                },
                rightButton: {
                  text: tCommon('Common_Save'),
                  onClick: triggerSubmit,
                  disabled: isSubmitting,
                  isLoading: isSubmitting,
                  hasLoaded: isFinished,
                },
              }
        }
      >
        <GeneralFormFiller
          form={form}
          resolver={studyNoteFillControlsResolver}
          defaultLayout={initialData?.layout ?? {}}
          initialScoreSections={initialData?.scoreSections ?? []}
          initialVariables={initialData?.variables ?? []}
          initialAnswers={initialData?.initialValue}
          initialAttachments={initialData?.initialAttachments}
        />
      </DialogModal>
      {confirmationModal.modal}
    </>
  );
};

const GeneralFormFiller: FC<GeneralFormFillerProps> = ({
  initialScoreSections,
  initialVariables,
  resolver,
  defaultLayout,
  initialAttachments,
  form,
}) => {
  return (
    <FormProvider {...form}>
      <ScoreContextProvider initialScoreSections={initialScoreSections} initialVariables={initialVariables}>
        <Editor resolver={resolver} enabled={false}>
          <AttachmentsContextProvider uploadFileFunction={undefined} initialAttachments={initialAttachments}>
            <Frame data={defaultLayout} />
          </AttachmentsContextProvider>
        </Editor>
      </ScoreContextProvider>
    </FormProvider>
  );
};
